/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import { Module } from '@msdyn365-commerce-modules/utilities';
import { CommerceProperty } from '@msdyn365-commerce/retail-proxy';
// import { getCheckoutState } from '@msdyn365-commerce/global-state';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { validateVatNum_CAPAsync } from '../../actions/DataActionExtension.g';
// import { getInvoiceAccountInfo_CAPAsync } from '../../actions/DataActionExtension.g';
import { ICheckoutInvoiceModuleProps, ICheckoutInvoiceViewProps } from './checkout-invoice';
import { convert_a2_to_a3 } from './common/convert_a2_a3';
import { CHECK_CODE_EVEN, CHECK_CODE_ODD, CHECK_CODE_CHARS } from './common/fiscal-code-utils';
import get from 'lodash/get';

// import { CartCheckout } from '@msdyn365-commerce-modules/checkout';

const allFields = [
    'billing',
    'InvoiceCustAccount_CAP',
    'Type_CAP',
    'Street_CAP',
    'StreetNumber_CAP',
    'ZipCode_CAP',
    'City_CAP',
    'Country_CAP',
    'OrgName_CAP',
    'FirstName_CAP',
    'LastName_CAP',
    'VatNum_CAP',
    'FiscalCode_CAP',
    'PEC_CAP',
    'SDI_CAP'
];

const updateExtProps = async (data: Inputs, moduleProps: ICheckoutInvoiceModuleProps) => {
    const dataCompiled = [];
    Object.keys(data).forEach(key => {
        const value =
            typeof get(data, key) === 'boolean'
                ? {
                      BooleanValue: get(data, key)
                  }
                : key === 'Type_CAP'
                ? {
                      IntegerValue: Number(get(data, key))
                  }
                : { StringValue: get(data, key) };
        dataCompiled.push({
            Key: key,
            Value: value
        });
    });

    for (const str of allFields) {
        if (!Object.keys(data).includes(str)) {
            dataCompiled.push({
                Key: str,
                Value: { StringValue: '' }
            });
        }
    }

    const checkoutResult = moduleProps.data.checkout.result;

    const dataCompiledCc = [
        {
            Key: 'CustomInvoiceModuleCompiled_VEN',
            Value: {
                BooleanValue: true
            }
        }
    ];

    Array.prototype.push.apply(dataCompiledCc, dataCompiled);

    if (checkoutResult) {
        await checkoutResult.checkoutCart.updateExtensionProperties({
            newExtensionProperties: dataCompiledCc
        });
    } else {
        console.error('Error in checkout-invoice.view.tsx - updateExtProps: Cannot write invoice details on the checkout cart');
    }
};

const _capDataRender = async (moduleProps: ICheckoutInvoiceModuleProps): Promise<[Inputs | null, boolean]> => {
    //const currentCheckoutState = await getCheckoutState(moduleProps.context.actionContext);
    const currentCheckoutState = moduleProps.data.checkout.result;

    const extProps = currentCheckoutState?.checkoutCart.cart?.ExtensionProperties;
    if (!extProps) {
        return [null, false];
    }
    const extPropsToRemoveA = extProps.filter(eProp => eProp.Key === 'ExtPropsRemove_CAP');
    const invoiceModuleCompiledA = extProps.filter(eProp => eProp.Key === 'CustomInvoiceModuleCompiled_VEN');
    const extPropsToRemove = extPropsToRemoveA && extPropsToRemoveA[0]?.Value?.BooleanValue;
    const invoiceModuleCompiled = (invoiceModuleCompiledA && invoiceModuleCompiledA[0]?.Value?.BooleanValue) ?? false;

    if (extPropsToRemove !== true) {
        return [convertExtPropertiesToInput(extProps), invoiceModuleCompiled];
    }
    return [null, false];
};

const convertExtPropertiesToInput = (extProps: CommerceProperty[]): Inputs => {
    const inputs: Inputs = {} as Inputs;
    extProps.forEach(prop => {
        if (prop.Key && prop.Value) {
            const values = [prop.Value.StringValue, prop.Value.IntegerValue, prop.Value.BooleanValue].filter(
                value => value !== undefined && value !== null
            );
            const value = values.length > 0 ? values[0] : null;
            if (value !== null && value !== undefined && value !== '') {
                if (allFields.indexOf(prop.Key) > -1) {
                    inputs[prop.Key] = value;
                }
            }
        }
    });
    // Return the inputs object
    return inputs;
};

const _capDataSave = async (moduleProps: ICheckoutInvoiceModuleProps, data: Inputs) => {
    await updateExtProps(data, moduleProps);
};

type Inputs = {
    City_CAP: string;
    FiscalCode_CAP: string;
    OrgName_CAP: string;
    PEC_CAP: string;
    SDI_CAP: string;
    StreetNumber_CAP: string;
    Street_CAP: string;
    VatNum_CAP: string;
    ZipCode_CAP: string;
    billing: boolean;

    Type_CAP: number;
    Country_CAP: string;
    InvoiceCustAccount_CAP: string;
    FirstName_CAP: string;
    LastName_CAP: string;
    [key: string]: string | number | boolean;
};

interface IMaincategories {
    key: string | undefined;
    value: string | undefined;
}
const countriesWithOptionalInvoice = [
    'AUT',
    'BEL',
    'BGR',
    'HRV',
    'CYP',
    'CZE',
    'DNK',
    'EST',
    'FIN',
    'FRA',
    'DEU',
    'GRC',
    'HRV',
    'HUN',
    'IRL',
    'ITA',
    'LVA',
    'LTU',
    'LUX',
    'MLT',
    'NLD',
    'POL',
    'PRT',
    'ROU',
    'SVK',
    'SVN',
    'ESP',
    'SWE',
    'GBR'
];

const validateFiscalCode15 = (codiceFiscale: string): boolean => {
    codiceFiscale = codiceFiscale.toUpperCase();
    let val = 0;
    for (let i = 0; i < 15; i = i + 1) {
        const c = codiceFiscale.charAt(i);
        val += i % 2 !== 0 ? CHECK_CODE_EVEN.get(c) ?? 0 : CHECK_CODE_ODD.get(c) ?? 0;
    }
    val = val % 26;
    return codiceFiscale.charAt(15) === CHECK_CODE_CHARS.charAt(val);
};
const validateFiscalCode11 = (codiceFiscale: string): boolean => {
    if (!/^[0-9]{11}$/.test(codiceFiscale)) {
        return false;
    }
    if (codiceFiscale === '00000000000') {
        return false;
    }
    let val = 0;
    for (let i = 0; i < 10; i = i + 1) {
        const c = parseInt(codiceFiscale.charAt(i), 10);
        //se sono dispari
        if (i % 2 === 0) {
            val += c;
        } else {
            let evenC = c * 2;
            if (evenC > 9) {
                evenC = evenC - 9;
            }
            val += evenC;
        }
    }
    val = 10 - (val % 10);
    val = val % 10;
    return codiceFiscale.charAt(10) === val.toString();
};

const validateFiscalCode = (codiceFiscale: string): boolean => {
    if (codiceFiscale.length === 16) {
        return validateFiscalCode15(codiceFiscale);
    }
    if (codiceFiscale.length === 11) {
        return validateFiscalCode11(codiceFiscale);
    }
    return false;
};

const CheckoutInvoiceView: React.FC<ICheckoutInvoiceViewProps> = props => {
    const { CheckoutInvoice, resources, countryRegions, moduleState, moduleProps, paymentVerificationRedirection } = props;
    const [dataCompiled, setDataCompiled] = React.useState<Inputs>();
    const shippingCountry = moduleProps?.data?.checkout?.result?.shippingAddress?.ThreeLetterISORegionName;
    const [billing, setBilling] = React.useState<boolean>(false);
    const [typeSelected, setTypeSelected] = React.useState<number>(0);
    const [countrySelected, setCountrySelected] = React.useState<string>('');
    const fiscalPattern = /^([A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST]{1}[0-9LMNPQRSTUV]{2}[A-Z]{1}[0-9LMNPQRSTUV]{3}[A-Z]{1})$|([0-9]{11})$/i;
    // @ts-ignore
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

    const customerId = CheckoutInvoice.moduleProps?.context?.actionContext?.requestContext?.user?.customerAccountNumber;
    const invoiceTypeOptions = [
        { key: '1', value: resources.typePersonLabel },
        { key: '2', value: resources.typeOrgLabel }
    ];

    React.useEffect(() => {
        void setData();
        // void getUserData();
    }, []);

    React.useEffect(() => {
        if (!paymentVerificationRedirection) {
            if (shippingCountry && countriesWithOptionalInvoice.indexOf(shippingCountry) === -1) {
                if (!billing) {
                    setBilling(true);
                    setValue('billing', true, { shouldDirty: true });
                    if (moduleState.status === 'ready') {
                        moduleState.onEdit && moduleState.onEdit();
                    }
                }
            } else if (typeSelected === 0) {
                setBilling(false);
                setValue('billing', false, { shouldDirty: true });
            }
        }
    }, [shippingCountry]);

    function gMapsAutocomplete() {
        setTimeout(() => {
            const boxes = document.querySelectorAll('.pac-container');
            boxes.forEach(box => box.remove());

            const autocompleteFormField = document.getElementsByName('Street_CAP')[0] as HTMLInputElement;
            if (autocompleteFormField === undefined || autocompleteFormField === null) {
                return;
            }

            // @ts-ignore
            const autocomplete = new window.google.maps.places.Autocomplete(autocompleteFormField, {
                types: ['address']
            });

            autocompleteFormField.setAttribute('placeholder', resources.startTypingYourAddress);

            // @ts-ignore
            var checkListeners = window.google.maps.event.hasListeners(autocomplete, `place_changed`);

            // @ts-ignore
            window.google.maps.event.clearInstanceListeners(autocomplete);

            // @ts-ignore
            const listener = window.google.maps.event.addListener(autocomplete, `place_changed`, () => {
                const place = autocomplete.getPlace();
                if (nativeInputValueSetter === undefined) {
                    return;
                }

                nativeInputValueSetter.call(autocompleteFormField, place.name);
                const eCustom = new Event('change', { bubbles: true });
                autocompleteFormField.dispatchEvent(eCustom);

                place.address_components.forEach((element: { types: string[]; long_name: string; short_name: string }) => {
                    var checkZipCode = false;
                    switch (element.types[0]) {
                        case 'street_number':
                            setValue('StreetNumber_CAP', element.long_name, {
                                shouldDirty: true
                            });
                            break;
                        case 'locality':
                            setValue('City_CAP', element.long_name, {
                                shouldDirty: true
                            });
                            break;
                        case 'country':
                            const countryValue = convert_a2_to_a3[element.short_name as keyof typeof convert_a2_to_a3];
                            setValue('Country_CAP', countryValue, { shouldDirty: true });
                            setCountrySelected(countryValue);
                            break;
                        case 'postal_code':
                            setValue('ZipCode_CAP', element.long_name, {
                                shouldDirty: true
                            });
                            checkZipCode = true;
                            break;
                        default:
                            break;
                    }
                    if (!checkZipCode) {
                        setValue('ZipCode_CAP', '', { shouldDirty: true });
                    }
                });
                const streetElements = document.getElementsByName('Street_CAP');
                const streetInput = streetElements[0] as HTMLInputElement;
                streetInput.value = streetInput.value.split(',')[0];
            });
        });
    }

    const { register, handleSubmit, errors, setValue, setError, reset } = useForm<Inputs>();

    const _renderInput = (name: string, label: string, required: boolean, errorMessage: string, pattern?: RegExp): JSX.Element => {
        return (
            <div className={`msc-address-form__item msc-address-form__item--${name}`} aria-invalid={get(errors, name) ? 'true' : 'false'}>
                <label key={`${name}-label-key`} htmlFor={name} className={'msc-address-form__label'}>
                    {label} {required && '*'}
                </label>
                <input
                    key={`${name}-input-key`}
                    id={name}
                    name={name}
                    ref={register({ required: required, pattern: pattern })}
                    className={'msc-address-form__input msc-address-form__input-text'}
                />
                {get(errors, name) && <span className={'error-message'}>{errorMessage}</span>}
            </div>
        );
    };

    const _renderTypeSelect = (name: string, label: string, options: IMaincategories[], required: boolean): JSX.Element => {
        return (
            <div className={'msc-address-form__item'} aria-invalid={get(errors, name) ? 'true' : 'false'}>
                <label key={`${name}-label-key`} htmlFor={name} className={'msc-address-form__label'}>
                    {label} {required && '*'}
                </label>
                <select
                    value={typeSelected}
                    key={`${name}-select-key`}
                    name={name}
                    className={'msc-address-form__dropdown'}
                    ref={register({ required: required })}
                    onChange={e => {
                        setValue('Type_CAP', e.target.value, {
                            shouldDirty: true
                        });
                        setTypeSelected(parseInt(e.target.value, 10));
                    }}
                >
                    <option key={`${name}-option-key`} aria-selected='true' value={0}>
                        {resources.selectOption}
                    </option>
                    {options.map(option => {
                        return (
                            <option key={option.key} aria-selected='false' value={option.key}>
                                {option.value}
                            </option>
                        );
                    })}
                    ;
                </select>
                {get(errors, name) && <span className={'error-message'}>{resources.genericFieldError}</span>}
            </div>
        );
    };

    const _renderCountrySelect = (name: string, label: string, options: IMaincategories[], required: boolean): JSX.Element => {
        return (
            <div className={'msc-address-form__item'} aria-invalid={get(errors, name) ? 'true' : 'false'}>
                <label key={`${name}-label-key`} htmlFor={name} className={'msc-address-form__label'}>
                    {label} {required && '*'}
                </label>
                <select
                    value={countrySelected}
                    key={`${name}-select-key`}
                    name={name}
                    className={'msc-address-form__dropdown'}
                    ref={register({ required: required })}
                    onChange={e => {
                        setCountrySelected(e.target.value);
                        setValue('Country_CAP', e.target.value, {
                            shouldDirty: true
                        });
                    }}
                >
                    <option key={`${name}-option-key`} aria-selected='true' value=''>
                        {resources.selectOption}
                    </option>
                    {options.map(option => {
                        return (
                            <option key={option.key} aria-selected='false' value={option.key}>
                                {option.value}
                            </option>
                        );
                    })}
                    ;
                </select>
                {get(errors, name) && <span className={'error-message'}>{resources.genericFieldError}</span>}
            </div>
        );
    };

    const onSubmit = async (data: Inputs) => {
        if (data.VatNum_CAP && data.VatNum_CAP !== '') {
            void validateVatNum_CAPAsync({ callerContext: moduleProps.context?.actionContext }, data.VatNum_CAP, data.Country_CAP).then(
                response => {
                    if (response.Status === true) {
                        if (data.Country_CAP === 'ITA' && data.FiscalCode_CAP && data.FiscalCode_CAP !== '') {
                            if (validateFiscalCode(data.FiscalCode_CAP)) {
                                setDataCompiled(data);
                                void _capDataSave(moduleProps, data);
                                moduleState.onReady();
                                reset(data);
                            } else {
                                setError('FiscalCode_CAP', {
                                    message: 'Fiscal Code Num not valid',
                                    shouldFocus: true
                                });
                            }
                        } else {
                            setDataCompiled(data);
                            void _capDataSave(moduleProps, data);
                            moduleState.onReady();
                            reset(data);
                        }
                    } else {
                        setError('VatNum_CAP', {
                            message: 'Vat Num not valid',
                            shouldFocus: true
                        });
                    }
                }
            );
        } else {
            if (data.Country_CAP === 'ITA' && data.FiscalCode_CAP && data.FiscalCode_CAP !== '') {
                if (validateFiscalCode(data.FiscalCode_CAP)) {
                    setDataCompiled(data);
                    void _capDataSave(moduleProps, data);
                    moduleState.onReady();
                    reset(data);
                } else {
                    setError('FiscalCode_CAP', {
                        message: 'Fiscal Code Num not valid',
                        shouldFocus: true
                    });
                }
            } else {
                setDataCompiled(data);
                void _capDataSave(moduleProps, data);
                moduleState.onReady();
                reset(data);
            }
        }
    };

    const _resetForm = () => {
        if (dataCompiled?.billing && !billing) {
            setBilling(true);
            setCountrySelected(dataCompiled.Country_CAP);
            setTypeSelected(dataCompiled.Type_CAP);
        } else if (dataCompiled && billing) {
            setCountrySelected(dataCompiled.Country_CAP);
            setTypeSelected(dataCompiled.Type_CAP);
        }
        moduleState.onReady();
    };

    const setData = async () => {
        const [dataLoadedFromExtProps, pageRefreshedDataSaved] = await _capDataRender(moduleProps);
        if (dataLoadedFromExtProps && pageRefreshedDataSaved && dataLoadedFromExtProps.billing !== undefined) {
            setDataCompiled(dataLoadedFromExtProps);
            setBilling(dataLoadedFromExtProps.billing);
            dataLoadedFromExtProps.Type_CAP && setTypeSelected(dataLoadedFromExtProps.Type_CAP);
            dataLoadedFromExtProps.Country_CAP && setCountrySelected(dataLoadedFromExtProps.Country_CAP);
            moduleState.onReady();
            reset(dataLoadedFromExtProps);
        }
    };

    // const getUserData = async () => {
    //     if (customerId) {
    //         await getInvoiceAccountInfo_CAPAsync({ callerContext: CheckoutInvoice.moduleProps.context?.actionContext }, customerId).then(
    //             result => {
    //                 // console.log('getInvoiceAccountInfo_CAPAsync');
    //                 // console.log(result);
    //             }
    //         );
    //     }
    // };

    if (moduleState.status === 'ready') {
        return (
            <Module {...CheckoutInvoice}>
                <div className='msc-address-detail'>
                    {dataCompiled &&
                        Object.keys(dataCompiled).map((keyName, i) => (
                            <span key={i} className={`msc-address-detail__item ${keyName}`}>
                                {get(dataCompiled, keyName)}
                                <br />
                            </span>
                        ))}
                </div>
            </Module>
        );
    } else {
        return (
            <Module {...CheckoutInvoice}>
                <div className={'msc-address-form__item inputBox privacy_field_ctn'} aria-invalid={errors.billing ? 'true' : 'false'}>
                    <label className={shippingCountry && countriesWithOptionalInvoice.indexOf(shippingCountry) === -1 ? 'hide' : 'show'}>
                        <input
                            type={'checkbox'}
                            disabled={!!(shippingCountry && countriesWithOptionalInvoice.indexOf(shippingCountry) === -1)}
                            name={'billing'}
                            checked={billing}
                            onChange={e => setBilling(e.target.checked)}
                            ref={register({ required: false })}
                        />
                        <div>{props.resources.billingLabel}</div>
                    </label>
                    {shippingCountry && countriesWithOptionalInvoice.indexOf(shippingCountry) === -1 && (
                        <p>
                            <strong>{props.config.billingLabel}</strong>
                        </p>
                    )}
                </div>
                {billing && (
                    <>
                        {props.config.invoiceNotIncludedLabel && (
                            <div className='ms-checkout__info-message'>
                                <p className='ms-checkout__info-message-body'>{props.config.invoiceNotIncludedLabel}</p>
                            </div>
                        )}
                        {gMapsAutocomplete()}
                        {customerId && (
                            <div className={'msc-address-form__item msc-address-form__item--InvoiceCustAccount_CAP'}>
                                <input
                                    aria-label='Customer Id'
                                    name={'InvoiceCustAccount_CAP'}
                                    value={customerId}
                                    ref={register()}
                                    className={'msc-address-form__input msc-address-form__input-text'}
                                />
                            </div>
                        )}
                        {_renderTypeSelect('Type_CAP', `${resources.typeLabel}`, invoiceTypeOptions, true)}
                        {typeSelected !== 0 &&
                            _renderInput('Street_CAP', `${resources.streetLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 &&
                            _renderInput('StreetNumber_CAP', `${resources.streetNumberLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 &&
                            _renderInput('ZipCode_CAP', `${resources.zipCodeLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 && _renderInput('City_CAP', `${resources.cityLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 && _renderCountrySelect('Country_CAP', `${resources.countryLabel}`, countryRegions, true)}
                        {typeSelected === 2 &&
                            _renderInput('OrgName_CAP', `${resources.orgNameLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected === 1 &&
                            _renderInput('FirstName_CAP', `${resources.firstNameLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected === 1 &&
                            _renderInput('LastName_CAP', `${resources.lastNameLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected === 2 &&
                            countriesWithOptionalInvoice.indexOf(countrySelected) > -1 &&
                            _renderInput('VatNum_CAP', `${resources.vatNumLabel}`, true, `${resources.vatNumberError}`)}
                        {typeSelected !== 0 &&
                            countrySelected === 'ITA' &&
                            _renderInput(
                                'FiscalCode_CAP',
                                `${resources.fiscalCodeLabel}`,
                                true,
                                `${resources.fiscalCodeError}`,
                                fiscalPattern
                            )}
                        {typeSelected !== 0 &&
                            countrySelected === 'ITA' &&
                            typeSelected === 2 &&
                            _renderInput('PEC_CAP', `${resources.pecLabel}`, true, `${resources.genericFieldError}`)}
                        {typeSelected !== 0 &&
                            countrySelected === 'ITA' &&
                            typeSelected === 2 &&
                            _renderInput('SDI_CAP', `${resources.sdiLabel}`, true, `${resources.genericFieldError}`)}
                    </>
                )}
                <div className='ms-checkout__guided-card-footer'>
                    <button
                        onClick={handleSubmit(onSubmit)}
                        type='button'
                        title='Save &amp; continue'
                        className={`ms-checkout__guided-card-btn-save msc-btn btn-primary ${
                            billing && typeSelected === 0 ? 'disable-save-billing' : ''
                        }`}
                        disabled={billing && typeSelected === 0}
                    >
                        {resources.saveContinueBtnLabel}
                    </button>
                    <button onClick={_resetForm} title='Cancel' className='ms-checkout__guided-card-btn-cancel msc-btn btn-secondary'>
                        {resources.cancelBtnLabel}
                    </button>
                </div>
            </Module>
        );
    }
};
export default CheckoutInvoiceView;
